import type { ManualChapter } from './types'

export const clinicChapter: ManualChapter = {
  id: 'clinic',
  number: 9,
  title: 'Compliance Clinic',
  description: 'Consulenze e supporto specialistico',
  icon: 'ShieldCheckIcon',
  sections: [
    {
      id: 'what-is',
      title: "Cos'e la Compliance Clinic",
      requirement: '§2.8 Accesso diretto a esperti via ticket',
      content: `
## Cos'e la Compliance Clinic

La Compliance Clinic e il servizio di **consulenza diretta** con gli esperti del Comitato Scientifico.

### A Cosa Serve

| Funzione | Descrizione |
|----------|-------------|
| **Consulenze** | Richiedi pareri su casi specifici |
| **Quesiti** | Poni domande tecniche agli esperti |
| **Supporto** | Ricevi assistenza su problematiche |

### Caratteristiche

- **Accesso diretto** agli esperti
- **Risposte qualificate** e tracciabili
- **Storico** delle consulenze
- **Allegati** per documentare i casi
- **Riservatezza** garantita

### Chi Sono gli Esperti

Il Comitato Scientifico e composto da:
- Professionisti certificati
- Esperti di settore
- Consulenti specializzati
`
    },
    {
      id: 'create-ticket',
      title: 'Aprire un Ticket',
      requirement: '§2.8 Richiesta consulenza con allegati',
      content: `
## Aprire un Ticket di Consulenza

### Procedura

1. Vai alla sezione **Compliance Clinic**
2. Clicca su **"Nuovo Ticket"**
3. Compila il form:

| Campo | Descrizione | Obbligatorio |
|-------|-------------|--------------|
| **Categoria** | Area tematica | Si |
| **Oggetto** | Titolo breve | Si |
| **Descrizione** | Dettagli del quesito | Si |
| **Priorita** | Urgenza della richiesta | Si |
| **Allegati** | Documenti correlati | No |

4. Clicca su **"Invia"**

### Categorie Disponibili

Le categorie dipendono dalla configurazione della piattaforma, ad esempio:
- Normativa
- Procedure
- Casi pratici
- Interpretazioni
- Altro

### Priorita

| Priorita | Tempo risposta indicativo |
|----------|---------------------------|
| **Bassa** | Entro 5 giorni lavorativi |
| **Media** | Entro 3 giorni lavorativi |
| **Alta** | Entro 1 giorno lavorativo |
| **Urgente** | Entro 4 ore |

> **Nota**: I tempi sono indicativi e dipendono dalla disponibilita degli esperti.

### Allegare Documenti

Per documentare meglio il tuo caso:
1. Clicca su **"Aggiungi allegato"**
2. Seleziona il file
3. Formati accettati: PDF, Word, Excel, immagini
4. Limite: 10 MB per file
`
    },
    {
      id: 'ticket-status',
      title: 'Stati del Ticket',
      content: `
## Stati del Ticket

### Ciclo di Vita

Un ticket attraversa diversi stati:

\`\`\`
Aperto → In Lavorazione → In Attesa → Risolto → Chiuso
\`\`\`

### Descrizione Stati

| Stato | Significato |
|-------|-------------|
| **Aperto** | Ticket inviato, in attesa di presa in carico |
| **In Lavorazione** | Un esperto sta lavorando al caso |
| **In Attesa** | Richieste informazioni aggiuntive |
| **Risolto** | Risposta fornita |
| **Chiuso** | Ticket archiviato |

### Notifiche

Riceverai notifica quando:
- Il ticket viene **preso in carico**
- L'esperto richiede **informazioni aggiuntive**
- Viene fornita una **risposta**
- Il ticket viene **chiuso**

### Ticket in Attesa

Se il ticket e "In Attesa", significa che l'esperto necessita di ulteriori informazioni:
1. Apri il ticket
2. Leggi la richiesta dell'esperto
3. Rispondi con le informazioni richieste
4. Il ticket tornera "In Lavorazione"
`
    },
    {
      id: 'responses',
      title: 'Risposte e Follow-up',
      requirement: '§2.8 Storico consulenze accessibile',
      content: `
## Risposte e Follow-up

### Visualizzare le Risposte

1. Vai su **Compliance Clinic** → **I miei ticket**
2. Clicca sul ticket
3. Leggi la risposta dell'esperto

### Conversazione

Il ticket funziona come una conversazione:
- **Tu** poni la domanda iniziale
- **L'esperto** risponde
- **Tu** puoi fare follow-up
- **L'esperto** risponde di nuovo
- ...e cosi via fino alla risoluzione

### Riaprire un Ticket

Se un ticket e stato chiuso ma hai bisogno di ulteriori chiarimenti:
1. Apri il ticket chiuso
2. Clicca su **"Riapri ticket"**
3. Aggiungi il tuo follow-up
4. Il ticket tornera in lavorazione

### Valutare la Risposta

Dopo la risoluzione puoi:
- Valutare la **qualita** della risposta
- Lasciare un **feedback**
- Questo aiuta a migliorare il servizio
`
    },
    {
      id: 'my-tickets',
      title: 'I Miei Ticket',
      content: `
## I Miei Ticket

### Visualizzare lo Storico

1. Vai su **Compliance Clinic**
2. Clicca su **"I miei ticket"**
3. Visualizza tutti i tuoi ticket

### Filtri

Puoi filtrare per:

| Filtro | Opzioni |
|--------|---------|
| **Stato** | Tutti, Aperti, In lavorazione, Risolti, Chiusi |
| **Categoria** | Per area tematica |
| **Data** | Ultimi 7 giorni, mese, anno |
| **Priorita** | Urgente, Alta, Media, Bassa |

### Ricerca

Usa la barra di ricerca per trovare ticket per:
- Oggetto
- Contenuto
- Numero ticket

### Export

Puoi esportare lo storico delle tue consulenze in formato PDF per:
- Archiviazione personale
- Riferimento futuro
- Documentazione audit
`
    },
    {
      id: 'best-practices',
      title: 'Best Practice',
      content: `
## Best Practice per i Ticket

### Come Scrivere un Buon Quesito

Per ricevere risposte piu accurate e veloci:

1. **Sii specifico**: Descrivi il caso concreto, non in astratto
2. **Fornisci contesto**: Spiega il background della situazione
3. **Allega documenti**: Se rilevanti per il caso
4. **Una domanda per ticket**: Non mescolare quesiti diversi
5. **Indica l'urgenza reale**: Usa la priorita corretta

### Esempio di Quesito Ben Formulato

> **Oggetto**: Interpretazione art. X in caso di Y
>
> **Descrizione**:
> Nel nostro contesto aziendale si e verificata la seguente situazione: [descrizione dettagliata].
>
> Abbiamo identificato le seguenti opzioni:
> 1. Opzione A
> 2. Opzione B
>
> Quale approccio e piu corretto secondo la normativa vigente?
>
> In allegato la documentazione di riferimento.

### Cosa Evitare

- Domande troppo generiche
- Quesiti multipli in un solo ticket
- Priorita urgente per casi non urgenti
- Allegati non pertinenti
`
    }
  ]
}
